// Contract detail (Session AAQ Loop 4). The dashboard renewals / price-rise tiles and the Contracts
// list now link here to a dedicated record view: the full contract, its linked quote, the attachment
// counts (what a delete is blocked on), and the actions that live on a contract -- generate a renewal
// letter and delete. Office/admin.
//
// TWO TABS since Session AQW (Phase 2, M-1): "Details" (default) and "Renewal", URL-bound (?tab=
// renewal; no param = Details), following the SAME idiom PersonnelPage.tsx already established for a
// URL-bound tab (searchParams, replace navigation, .ele-tabs/.ele-tab). This is a PRESENTATION-ONLY
// consolidation: the three renewal mechanisms this contract can go through (the interactive customer
// link -- RegisterConfirmationPanel/RenewalLinkPanel/RenewalFrequencyPanel/RenewalPriceOverridePanel;
// the CPI-only proposal ledger on RenewalsPage.tsx; the manual covering-letter RenewalModal) are
// UNCHANGED underneath -- only where their triggers and panels live on this page moved. The
// renewal-cycle-needs-attention alert stays ABOVE the tab bar, visible on both tabs, because it is
// the one place urgency must not be hidden behind a tab click.
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, ApiError, type ContractDetail, type ContractCoverage, type RenewalCycleState } from "../../lib/api";
import { ConfirmDeleteModal } from "../../components/ConfirmDeleteModal";
import { ConfirmButton } from "../../components/ConfirmButton";
import { RenewalDisclosurePanel } from "./RenewalDisclosurePanel";
import { ModalShell, MODAL_ACTIONS, MODAL_ACTION_BTN } from "../../components/ModalShell";
import { OnboardingChecklist } from "../../components/OnboardingChecklist";
import { RenewalModal } from "./ContractsPage";
import { ManageLiftsModal } from "./ManageLiftsModal";
import { StartServiceNowModal } from "./StartServiceNowModal";
import { CancelContractModal } from "./CancelContractModal";
import { RegisterConfirmationPanel } from "./RegisterConfirmationPanel";
import { RenewalLinkPanel } from "./RenewalLinkPanel";
import { RenewalFrequencyPanel } from "./RenewalFrequencyPanel";
import { RenewalPriceOverridePanel } from "./RenewalPriceOverridePanel";
import { XeroBillingPanel } from "./XeroBillingPanel";
import { ReplacementAgreementSection } from "./ReplacementAgreementSection";
import { DefaultEngineerCard } from "../../components/DefaultEngineerCard";
import { useAuth } from "../../lib/auth";
import { Button } from "../../components/Button";
import { RecordHeader } from "../../components/RecordHeader";
import { Loader } from "../../components/Loader";
import { StatusPill } from "../../components/StatusPill";
import { formatDate } from "../../lib/format";

const TIER_LABEL: Record<string, string> = { core: "Core", care: "Care", complete: "Complete" };
const FREQ_LABEL: Record<string, string> = {
  monthly: "Monthly",
  bi_monthly: "Every 2 months",
  quarterly: "Quarterly",
  six_monthly: "Twice yearly",
  annual: "Annual",
};
// Equipment cadences in the customer-facing wording (Session ACR).
const EQUIPMENT_FREQ: Record<string, string> = {
  annual: "Yearly",
  six_monthly: "Six-monthly",
  quarterly: "Quarterly",
  bi_monthly: "Every 2 months",
  monthly: "Monthly",
};

function gbp(n: number): string {
  return `£${Math.round(n).toLocaleString("en-GB")}`;
}
// Per-period invoice amounts keep their pence (£173.25 and £247.50 are different invoices).
function gbpExact(n: number): string {
  return `£${n.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
// The shared date, or "Not set" for a missing one (the helper itself prints "-"). The shared helpers
// are pinned to Europe/London, and a DATE-ONLY value stored at UTC midnight of the London day (the
// termination-notice dates) is always the same calendar day there, so it can never read a day early
// for anyone west of Greenwich, a one-day disagreement with the letter the customer holds in the
// direction of ending cover a day early (AHN review fix, now carried by the helper itself).
function dateOrNotSet(iso: string | null | undefined): string {
  return iso ? formatDate(iso) : "Not set";
}
// The renewal-cycle findings shown in the danger treatment rather than amber: a review never actioned,
// and (Session APA) a CPI rise applied without the customer being told, which is a customer about to be
// invoiced at a price nobody gave them. Stalls and waiting proposals stay amber: housekeeping.
function renewalCycleUrgent(state: RenewalCycleState): boolean {
  return state === "missed_rolled_past" || state === "missed_overdue" || state === "priced_not_confirmed_with_customer";
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 border-b border-ele-hairline-soft py-2 sm:flex-row sm:items-baseline sm:justify-between">
      <span className="font-menu text-xs uppercase tracking-wide text-ele-body">{label}</span>
      <span className="text-sm text-ele-strong">{value}</span>
    </div>
  );
}

// Session ARA (S-4): self-fetching, following the SiteDetailPage.tsx SiteTrainingCard idiom.
// SELF-fetching, deliberately, rather than reading a field off `ContractDetail`: this session's
// scope kept contract.routes.ts's general GET/PATCH /:id (and its DTO, ContractDetail) untouched
// -- see the header of contractDefaultEngineer.routes.ts for why -- so this card owns its own
// small GET/PATCH round trip via the separately-registered route instead.
function ContractDefaultEngineerSection({ contractId, canEdit }: { contractId: string; canEdit: boolean }) {
  const [value, setValue] = useState<string | null | undefined>(undefined); // undefined = still loading
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoadError(false);
    // async/await + try/catch, not just a `.then` rejection handler: a test fixture mocking `api`
    // without this method throws SYNCHRONOUSLY on the call below, which a rejection handler alone
    // would not catch (the same DefaultEngineerCard.tsx guard, for the same reason).
    (async () => {
      try {
        const r = await api.contracts.defaultEngineer(contractId);
        if (alive) setValue(r.defaultEngineerId);
      } catch {
        if (alive) setLoadError(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [contractId]);

  if (loadError) {
    return (
      <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
        <h2 className="font-heading text-lg text-ele-blue">Default engineer</h2>
        <p role="alert" className="mt-2 text-sm text-ele-strong">
          Could not load the default engineer. Refresh to try again.
        </p>
      </section>
    );
  }

  return (
    <DefaultEngineerCard
      value={value}
      canEdit={canEdit}
      description="Overrides the site's own default for THIS contract only, for the rare site with more than one contract needing different engineers. Leave unset to use the site's default."
      onSave={async (defaultEngineerId) => {
        const updated = await api.contracts.updateDefaultEngineer(contractId, defaultEngineerId);
        setValue(updated.defaultEngineerId);
      }}
    />
  );
}

type DetailTab = "details" | "renewal";
const TABS: Array<{ value: DetailTab; label: string }> = [
  { value: "details", label: "Details" },
  { value: "renewal", label: "Renewal" },
];

export function ContractDetailPage() {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // Session ARA (S-4): the default-engineer control is office/admin, matching the guard on its
  // own route (contractDefaultEngineer.routes.ts) -- the SiteDetailPage.tsx precedent's canEdit.
  const canEdit = isAdmin || user?.role === "office";
  const [contract, setContract] = useState<ContractDetail | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "notfound" | "error">("loading");
  const [renewOpen, setRenewOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Session ACF: lift coverage (covered lifts + amendment history) + the "Manage lifts" modal.
  const [coverage, setCoverage] = useState<ContractCoverage | null>(null);
  // Session AEH: distinguishes "nothing to show yet" from "the load actually failed", so a failed
  // fetch renders an inline error + Retry instead of the section silently vanishing.
  const [coverageLoadFailed, setCoverageLoadFailed] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  // Session AMC: "Start a service now" -- an immediate, one-off visit today, separate from the
  // generated PPM schedule.
  const [startServiceNowOpen, setStartServiceNowOpen] = useState(false);
  const [revertBusyId, setRevertBusyId] = useState<string | null>(null);
  const [coverageError, setCoverageError] = useState<string | null>(null);
  // Session AER: ending cover. This is the only thing that opens the 30-day call-out grace window.
  const [endOpen, setEndOpen] = useState(false);
  const [endBusy, setEndBusy] = useState(false);
  const [endError, setEndError] = useState<string | null>(null);
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endReason, setEndReason] = useState("");
  // Session AHN: formal cancellation with written notice (letter PDF + Gmail draft), and the
  // withdraw control for a pending Clause 5.1 notice.
  const [cancelOpen, setCancelOpen] = useState(false);
  const [withdrawBusy, setWithdrawBusy] = useState(false);
  const [withdrawError, setWithdrawError] = useState<string | null>(null);
  const [withdrawNote, setWithdrawNote] = useState<string | null>(null);

  // Session AQW: the Details/Renewal tab, in the URL (?tab=renewal), mirroring PersonnelPage.tsx's
  // established pattern for a tab a deep link needs to name -- the "Renewal due" row below and every
  // Renewals-list row now link straight at ?tab=renewal, so component state alone could not carry it.
  const [searchParams, setSearchParams] = useSearchParams();
  const tab: DetailTab = searchParams.get("tab") === "renewal" ? "renewal" : "details";
  const selectTab = useCallback(
    (next: DetailTab) => {
      if (next === tab) return;
      const params = new URLSearchParams(searchParams);
      if (next === "details") params.delete("tab");
      else params.set("tab", next);
      // replace: a tab switch is not a place the back button should have to step through.
      setSearchParams(params, { replace: true });
    },
    [tab, searchParams, setSearchParams],
  );

  async function withdrawNotice() {
    setWithdrawBusy(true);
    setWithdrawError(null);
    try {
      const res = await api.contracts.cancellationWithdraw(id);
      setWithdrawNote(res.message);
      await load();
    } catch (err) {
      setWithdrawError(
        err instanceof ApiError && err.message ? err.message : "Could not withdraw the notice. Please try again.",
      );
    } finally {
      setWithdrawBusy(false);
    }
  }

  async function endContract() {
    setEndBusy(true);
    setEndError(null);
    try {
      await api.contracts.end(id, { effectiveDate: endDate, reason: endReason.trim() });
      setEndOpen(false);
      setEndReason("");
      // Re-read rather than patching in place: the grace state is derived server-side, and this page
      // must never show a locally guessed version of a billing rule.
      await load();
    } catch (err) {
      setEndError(
        err instanceof ApiError && err.message ? err.message : "Could not end this contract. Please try again.",
      );
    } finally {
      setEndBusy(false);
    }
  }

  async function reopenContract() {
    setEndBusy(true);
    setEndError(null);
    try {
      await api.contracts.reopen(id);
      await load();
    } catch (err) {
      setEndError(
        err instanceof ApiError && err.message ? err.message : "Could not reopen this contract. Please try again.",
      );
    } finally {
      setEndBusy(false);
    }
  }

  const loadCoverage = useCallback(async () => {
    setCoverageLoadFailed(false);
    try {
      setCoverage(await api.contracts.coverage(id));
    } catch {
      setCoverage(null);
      setCoverageLoadFailed(true);
    }
  }, [id]);

  const load = useCallback(async () => {
    setState("loading");
    try {
      setContract(await api.contracts.get(id));
      setState("ready");
      void loadCoverage();
    } catch (err) {
      setState(err instanceof ApiError && err.status === 404 ? "notfound" : "error");
    }
  }, [id, loadCoverage]);

  const revertAmendment = useCallback(
    async (amendmentId: string) => {
      setRevertBusyId(amendmentId);
      setCoverageError(null);
      try {
        await api.contracts.coverageRevert(id, amendmentId);
        await loadCoverage();
        setContract(await api.contracts.get(id)); // the annual value on the header changes too
      } catch (err) {
        setCoverageError(err instanceof ApiError ? err.message || err.code : "Could not revert that amendment.");
      } finally {
        setRevertBusyId(null);
      }
    },
    [id, loadCoverage],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmDelete() {
    if (!contract) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await api.contracts.remove(contract.id);
      navigate("/office/contracts");
    } catch (err) {
      setDeleteError(err instanceof ApiError ? err.message || err.code : "Could not delete the contract. Please try again.");
      setDeleteBusy(false);
    }
  }

  if (state === "loading") return <Loader shape="panel" className="p-6" />;
  if (state === "notfound") return <p className="p-6 text-center text-ele-body">That contract could not be found.</p>;
  if (state === "error" || !contract) return <p role="alert" className="p-6 text-center text-ele-strong">Could not load this contract. Please try again.</p>;

  const c = contract;
  const hasBlockers = c.status === "active" || c.counts.invoices > 0 || c.counts.jobs > 0 || c.counts.lolerReports > 0;

  return (
    <div className="mx-auto max-w-4xl px-4 py-6">
      <RecordHeader
        className="mb-4"
        backTo="/office/contracts"
        backLabel="Back to contracts"
        title={
          c.siteId ? (
            <Link to={`/office/sites/${encodeURIComponent(c.siteId)}`} className="underline-offset-4 transition hover:text-ele-green hover:underline">
              {c.siteName}
            </Link>
          ) : (
            c.siteName
          )
        }
        status={<StatusPill domain="contract" status={c.status} />}
        actions={
          c.status === "active" && tab !== "renewal" ? (
            <Button variant="secondary" size="sm" onClick={() => selectTab("renewal")}>
              Start renewal
            </Button>
          ) : undefined
        }
        subtitle={
          c.customerId ? (
            <Link to={`/office/customers/${encodeURIComponent(c.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
              {c.customerName}
            </Link>
          ) : (
            c.customerName
          )
        }
      />

      {/* Session AMW: the renewal-cycle fail-safe, inline on the one record. Derived live on every
          read from the contract's first term end and the proposals ledger, never from the "Renewal
          due" row below (which can have been moved past a review nobody actioned) and never from the
          notes. Shown only when there is something to act on; a healthy cycle says nothing here.
          Session AQW: kept ABOVE the tab bar, deliberately, so a review nobody actioned is never one
          tab click away from being missed a second time. */}
      {c.renewalCycle && !c.renewalCycle.healthy ? (
        <section
          role="alert"
          className={`mb-4 rounded-ele border p-5 ${
            renewalCycleUrgent(c.renewalCycle.state) ? "border-ele-danger bg-ele-surface" : "border-amber-500 bg-amber-500/10"
          }`}
        >
          <h2 className={`font-heading text-lg ${renewalCycleUrgent(c.renewalCycle.state) ? "text-ele-danger" : "text-amber-300"}`}>
            Renewal cycle needs attention
          </h2>
          <p className="mt-1 text-sm text-ele-strong">{c.renewalCycle.message}</p>
          <p className="mt-2 text-xs text-ele-body">
            Checked live from this contract&apos;s first term end
            {c.renewalCycle.lastDueAnniversary ? ` (last anniversary ${dateOrNotSet(c.renewalCycle.lastDueAnniversary)})` : ""} and the proposals
            ledger{c.renewalCycle.state === "priced_not_confirmed_with_customer" ? ", and the office mailbox for what reached the customer" : ""},
            never from the renewal date field or the notes.
          </p>
        </section>
      ) : null}

      {/* Session BAK: the office's record that the customer was told the new charge. Shown while the finding is open
          and for as long as any record exists, so a record can always be removed. */}
      {c.status === "active" ? (
        <RenewalDisclosurePanel
          contractId={c.id}
          findingOpen={c.renewalCycle?.state === "priced_not_confirmed_with_customer"}
          onChanged={() => void load()}
        />
      ) : null}

      {/* Session AQW (M-1): the Details/Renewal tab bar. Same quiet text-tab treatment as
          PersonnelPage.tsx (.ele-tabs / .ele-tab, purple underline on the active tab only). */}
      <div className="ele-tabs mb-5">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => selectTab(t.value)}
            aria-pressed={tab === t.value}
            className={`ele-tab ${tab === t.value ? "is-active" : ""}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "details" ? (
        <>
          <section className="mb-4 grid gap-x-6 rounded-ele border border-ele-purple-accent bg-ele-surface p-5 sm:grid-cols-2">
            <Row label="Tier" value={TIER_LABEL[c.tier] ?? c.tier} />
            <Row label="Contract type" value={c.contractType} />
            <Row label="Annual value" value={`${gbp(c.annualValue)} ex VAT`} />
            <Row label="Per visit" value={c.visitValue == null ? "Not set" : `${gbp(c.visitValue)} ex VAT`} />
            <Row label="Billing" value={FREQ_LABEL[c.billingFrequency] ?? c.billingFrequency} />
            {/* escalatorPct is the operational uplift over CPI (Session ADU), so show it as "CPI + X%"
                to match the signed ESA, not a bare "X%" that reads as the whole annual increase. */}
            <Row label="Escalator" value={c.escalatorPct == null ? "Not set" : `CPI + ${c.escalatorPct}%`} />
            <Row label="Commenced" value={dateOrNotSet(c.commencedAt)} />
            <Row label="Start date" value={dateOrNotSet(c.startDate)} />
            <Row label="Initial term end" value={dateOrNotSet(c.initialTermEnd)} />
            {/* Session AQW: "Open renewal" jumps straight to the Renewal tab -- the one place every
                renewal mechanism for this contract now lives. */}
            <Row
              label="Renewal due"
              value={
                <span className="inline-flex flex-wrap items-center gap-2">
                  <span>{c.renewalDueSoon ? `${dateOrNotSet(c.renewalDate)} (within 90 days)` : dateOrNotSet(c.renewalDate)}</span>
                  <button
                    type="button"
                    onClick={() => selectTab("renewal")}
                    className="font-menu text-xs tracking-wide text-ele-green underline-offset-2 hover:underline"
                  >
                    Open renewal
                  </button>
                </span>
              }
            />
            {/* Session AKF: the platform's own recurring-invoicing sweep is retired (see the note
                below), so this stored flag no longer drives anything -- shown as a plain historical
                fact, never a claim about current behaviour. */}
            <Row
              label="Recurring invoicing"
              value={c.xeroAutoInvoice ? "Recorded on (retired: the platform no longer raises these; see Xero billing below)" : "Off (see Xero billing below)"}
            />
            <Row label="Created" value={dateOrNotSet(c.createdAt)} />
            <Row label="Customer billing email" value={c.customerEmail ?? "None on file"} />
            <Row label="Site address" value={c.siteAddress ?? "Not recorded"} />
            {/* Session AQW (C-6): links to the Quote Hub when a real quote id is on the DTO (it
                already is -- ContractDetail.quoteId, carried since Session ADH); plain text otherwise. */}
            <Row
              label="From quote"
              value={
                c.quoteId && c.quoteRef ? (
                  <Link
                    to={`/office/quotes/${encodeURIComponent(c.quoteId)}`}
                    className="text-ele-green underline-offset-2 hover:underline"
                  >
                    {c.quoteRef}
                  </Link>
                ) : (
                  c.quoteRef ?? "Not linked"
                )
              }
            />
            {/* Session AJG: both DERIVED SERVER-SIDE on every read from the platform's own records, not
                read from the Notes block below. Before this, the only place either fact appeared was
                that free-text note, which the platform wrote once at contract creation and never
                updated, so six live contracts read "Awaiting signature." about contracts signed weeks
                or months earlier. Deliberately plain Rows rather than a toned section: they are ordinary
                facts about the contract, not a termination notice. */}
            <Row
              label="ESA signature"
              value={c.signature.signedAt ? `${c.signature.label} on ${dateOrNotSet(c.signature.signedAt)}` : c.signature.label}
            />
            <Row
              label="First invoice"
              value={c.firstInvoice.paidAt ? `${c.firstInvoice.label} (${dateOrNotSet(c.firstInvoice.paidAt)})` : c.firstInvoice.label}
            />
          </section>

          {/* Session AQW (W-11): the new-contract onboarding checklist. Every item derived live
              server-side; nothing here is stored a second time (RULE 6). */}
          {c.onboarding && <OnboardingChecklist items={c.onboarding} />}

          {/* Session ARA (S-4): built to D84's standing RECOMMENDATION, not a confirmed Director
              ruling -- see docs/ELE-V2-DIRECTOR-DECISIONS-2026-07-19.md. */}
          <ContractDefaultEngineerSection contractId={id} canEdit={canEdit} />

          {/* Session AER: cover status. When a contract has been ended this is the first thing the
              office needs, because it decides what a call-out costs the customer today. inLapseGrace and
              graceLastDay are computed by the server from the same helper the billing resolver uses, so
              this panel cannot drift from the rate actually charged. */}
          {c.lapsedAt ? (
            <section
              className={`mb-4 rounded-ele border p-5 ${c.inLapseGrace ? "border-amber-500 bg-amber-500/10" : "border-ele-purple-accent bg-ele-surface"}`}
            >
              <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                <h2 className={`font-heading text-lg ${c.inLapseGrace ? "text-amber-300" : "text-ele-strong"}`}>Cover ended</h2>
                {isAdmin && (
                  <ConfirmButton
                    onConfirm={() => void reopenContract()}
                    busy={endBusy}
                    busyLabel="Reopening..."
                    label="Reopen contract"
                    confirmLabel="Press again to reopen"
                    className="rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                  />
                )}
              </div>
              <div className="space-y-1 text-sm text-ele-body">
                <p>
                  Cover ended on <span className="text-ele-strong">{dateOrNotSet(c.lapsedAt)}</span>.
                </p>
                {c.endReason && <p>Reason: {c.endReason}</p>}
                {c.inLapseGrace ? (
                  <p className="text-amber-300">
                    Call-outs still bill at the last agreed contract rate until{" "}
                    <span className="font-semibold">{dateOrNotSet(c.graceLastDay)}</span>. After that they bill at the published
                    non-contract rate.
                  </p>
                ) : (
                  <p>The 30 day grace period has passed, so call-outs now bill at the published non-contract rate.</p>
                )}
              </div>
              {endError && (
                <p role="alert" className="mt-3 text-sm text-ele-danger">
                  {endError}
                </p>
              )}
            </section>
          ) : (
            isAdmin &&
            c.status === "active" &&
            !c.cancellation?.pendingNotice && (
              <section className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
                <div>
                  <h2 className="font-heading text-lg text-ele-strong">Cover</h2>
                  <p className="text-sm text-ele-body">
                    Maintenance cover is running. End contract records that cover has already stopped (the customer gave notice).
                    Cancel contract issues OUR formal written notice, with the letter and a Gmail draft prepared for you.
                  </p>
                </div>
                {/* Session AHN: two quiet secondary actions, deliberately not primary-styled -- ending
                    or cancelling a contract is a rare, consequential act, never an everyday one. */}
                <div className="flex flex-wrap gap-2">
                  {/* max-sm:min-h-11: the page's own ACW 44px mobile floor. These two adjacent quiet
                      actions have OPPOSITE legal meanings (recording the customer's notice vs issuing
                      ours), so a phone mis-tap between them is the worst place to save 11px (review). */}
                  <button
                    onClick={() => {
                      setEndError(null);
                      setEndOpen(true);
                    }}
                    className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                  >
                    End contract
                  </button>
                  <button
                    onClick={() => setCancelOpen(true)}
                    className="max-sm:min-h-11 rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-danger hover:text-ele-danger"
                  >
                    Cancel contract
                  </button>
                </div>
              </section>
            )
          )}

          {/* Session AHN: a termination notice has been issued but its notice period is still running.
              The contract stays active until the effective date (the letter promises visits continue);
              the daily sweep ends it on the day. Withdraw clears the platform's record of the notice. */}
          {c.cancellation?.pendingNotice && c.status === "active" && (
            <section className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 p-5">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-3">
                <h2 className="font-heading text-lg text-amber-300">Termination notice issued</h2>
                {isAdmin && (
                  <ConfirmButton
                    onConfirm={() => void withdrawNotice()}
                    busy={withdrawBusy}
                    busyLabel="Withdrawing..."
                    label="Withdraw notice"
                    confirmLabel="Press again to withdraw"
                    className="rounded-ele border border-ele-purple-accent px-3 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green"
                  />
                )}
              </div>
              {/* text-amber-300, not -200: the AFK ink patch themes amber-300/400 through the
                  --ele-amber-text tokens, so this stays readable on the light shell. The termination
                  date is the one fact this panel exists to state (review fix). */}
              <div className="space-y-1 text-sm text-amber-300">
                <p>
                  Notice issued <span className="font-semibold">{dateOrNotSet(c.cancellation.pendingNotice.noticeAt)}</span>:{" "}
                  {c.cancellation.pendingNotice.reasonLabel}.
                </p>
                <p>
                  Cover ends <span className="font-semibold">{dateOrNotSet(c.cancellation.pendingNotice.effectiveAt)}</span>. Until then the
                  contract stays active, exactly as the letter promises; it ends automatically on that date.
                </p>
                {c.cancellation.pendingNotice.notes && <p>Internal notes: {c.cancellation.pendingNotice.notes}</p>}
              </div>
              {withdrawError && (
                <p role="alert" className="mt-3 text-sm text-ele-danger">
                  {withdrawError}
                </p>
              )}
            </section>
          )}

          {withdrawNote && (
            <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-4">
              <p className="text-sm text-ele-body">{withdrawNote}</p>
            </section>
          )}

          {/* Session ACN: the promo billing plan, office-only. V2 creates ONE first-period Xero DRAFT
              (already at the discounted rate); the office converts it into a Xero recurring invoice by
              hand, and that recurring template MUST be the standard rate or the promo repeats forever.
              All figures come from the server (the same shared helper that priced the first DRAFT). */}
          {c.promoPlan && (
            <section className="mb-4 rounded-ele border border-amber-500 bg-amber-500/10 p-5">
              <h2 className="mb-2 font-heading text-lg text-amber-300">Promotional discount: billing plan</h2>
              <div className="space-y-2 text-sm text-amber-300">
                <p>
                  Promo: {c.promoPlan.pct}% off for the first {c.promoPlan.months} months. That is the first{" "}
                  <span className="font-semibold">{c.promoPlan.promoPeriods}</span> {c.promoPlan.periodLabel}{" "}
                  {c.promoPlan.promoPeriods === 1 ? "invoice" : "invoices"}.
                  {c.promoPlan.reason ? ` Reason: ${c.promoPlan.reason}.` : ""}
                </p>
                <p>
                  Discounted per-period amount: <span className="font-semibold">{gbpExact(c.promoPlan.discountedPeriodAmount)}</span>. Standard
                  per-period amount: <span className="font-semibold">{gbpExact(c.promoPlan.fullPeriodAmount)}</span>. Both ex VAT.
                </p>
                {c.xeroAutoInvoice ? (
                  <p>
                    This contract is enrolled in the platform's recurring invoicing, which prices each period itself: the promo
                    window's {c.promoPlan.promoPeriods === 1 ? "period" : "periods"} at the discounted rate, every later period at
                    the standard rate. No Xero recurring template is needed - do not create one, or the customer would be
                    invoiced twice.
                  </p>
                ) : (
                  <p>
                    Invoice 1 was created as a DRAFT at the discounted rate. When you set up the RECURRING invoice in Xero, use the
                    STANDARD rate {gbpExact(c.promoPlan.fullPeriodAmount)} and issue the first {c.promoPlan.promoPeriods}{" "}
                    {c.promoPlan.promoPeriods === 1 ? "period" : "periods"} at the discounted rate{" "}
                    {gbpExact(c.promoPlan.discountedPeriodAmount)}. Do NOT set the recurring template to the discounted amount or the
                    promo will never end.
                  </p>
                )}
              </div>
            </section>
          )}

          {/* Session AKF (18 Aug 2026, Director decision, docs/AIN-DIRECTOR-DECISIONS-17-08-26.md):
              recurring contract billing stays on Xero's own repeating invoice templates, never the
              platform. This used to be an enrolment toggle proposing invoices into the office
              "Invoices due" batch; that page and the daily proposal sweep behind it are both retired
              (server/src/events/subscribers.ts, "contract-invoicing"). Session ARY (Item 4) replaces
              the plain statement of where billing happens with the panel that actually checks it:
              linking this contract to the Xero repeating invoice(s) that bill it and deriving a live
              verdict on every read, never writing to Xero itself. */}
          {/* Keyed on what the check compares, so a price, cadence or status change made on this page (a coverage
              change, a renewal confirmed) re-reads the verdict instead of showing the one from before it. */}
          <XeroBillingPanel key={`${c.id}:${c.annualValue}:${c.billingFrequency}:${c.status}`} contractId={c.id} />
          {/* Session BBZ: put a legacy-paper contract onto the current ESA at its current price (a draft quotation). */}
          {c.status === "active" && <ReplacementAgreementSection contractId={c.id} />}

          {c.notes && (
            <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
              <h2 className="mb-2 font-heading text-lg text-ele-strong">Notes</h2>
              <p className="whitespace-pre-wrap text-sm text-ele-body">{c.notes}</p>
            </section>
          )}

          {/* Session AQW (C-6): invoices and LOLER reports link through to their own list, pre-filtered
              by the convention each list ALREADY supports (checked before linking, not guessed):
              InvoicesPage reads only ?customerId= (customerFilter.tsx), LolerReportsPage reads only
              ?siteId=. Neither offers a per-CONTRACT filter, so these are customer-/site-scoped rather
              than scoped to exactly this one contract -- the closest real, working filter, not a guess.
              Jobs has NO working forward-filter at all: JobSheetList/JobSheetsPage only deep-links a
              single sheet by id (?job=<jobId>), so it stays plain text rather than a link to an
              unfiltered, company-wide list, which would be misleading about what it shows. */}
          <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
            <h2 className="mb-2 font-heading text-lg text-ele-strong">Attached records</h2>
            <p className="text-sm text-ele-body">
              {c.customerId ? (
                <Link to={`/office/invoices?customerId=${encodeURIComponent(c.customerId)}`} className="text-ele-green underline-offset-2 hover:underline">
                  {c.counts.invoices} {c.counts.invoices === 1 ? "invoice" : "invoices"}
                </Link>
              ) : (
                `${c.counts.invoices} ${c.counts.invoices === 1 ? "invoice" : "invoices"}`
              )}
              {", "}
              {c.counts.jobs} {c.counts.jobs === 1 ? "job" : "jobs"}
              {", "}
              {c.siteId ? (
                <Link to={`/office/loler?siteId=${encodeURIComponent(c.siteId)}`} className="text-ele-green underline-offset-2 hover:underline">
                  {c.counts.lolerReports} LOLER {c.counts.lolerReports === 1 ? "report" : "reports"}
                </Link>
              ) : (
                `${c.counts.lolerReports} LOLER ${c.counts.lolerReports === 1 ? "report" : "reports"}`
              )}{" "}
              on file.
            </p>
          </section>

          {coverageLoadFailed && (
            <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
              <h2 className="mb-2 font-heading text-lg text-ele-strong">Lift coverage</h2>
              <p role="alert" className="text-sm text-ele-danger">Could not load lift coverage.</p>
              <button
                onClick={() => void loadCoverage()}
                className="mt-2 rounded-ele border border-ele-purple-accent px-3 py-1.5 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong"
              >
                Retry
              </button>
            </section>
          )}

          {/* Session ACF: lift coverage -- which lifts this contract covers, plus the forward-only
              amendment history. Admins can add/remove lifts and revert the most recent amendment. */}
          {coverage && (
            <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <h2 className="font-heading text-lg text-ele-strong">Lift coverage</h2>
                <div className="flex flex-wrap items-center gap-2">
                  {/* Session AMC: separate from the generated PPM schedule, for the day-of-signing
                      overdue-catch-up case (docs/ELE-V2-NEW-ITEMS-17-08-26.md item 3). */}
                  {coverage.covered.length > 0 && c.status === "active" && (
                    <button
                      onClick={() => setStartServiceNowOpen(true)}
                      className="rounded-ele border border-ele-blue px-3 py-1.5 font-menu text-xs tracking-wide text-ele-blue hover:bg-ele-blue/10"
                    >
                      Start a service now
                    </button>
                  )}
                  {isAdmin && (
                    <Button variant="secondary" size="sm" onClick={() => setManageOpen(true)}>
                      Manage lifts
                    </Button>
                  )}
                </div>
              </div>
              {coverage.covered.length === 0 ? (
                <p className="text-sm text-ele-body">No lifts are currently covered by this contract.</p>
              ) : (
                <p className="text-sm text-ele-body">
                  {coverage.covered.length} {coverage.covered.length === 1 ? "lift" : "lifts"} covered:{" "}
                  {/* Forward click-through to each covered lift (Session ADI): the ELE numbers were plain
                      text, now each links to its lift detail (blue reference treatment). */}
                  {coverage.covered.map((l, i) => (
                    <span key={l.id}>
                      {i > 0 ? ", " : ""}
                      <Link to={`/office/lifts/${encodeURIComponent(l.id)}`} className="ele-ref underline-offset-2 hover:underline">
                        {l.eleNumber ?? "Lift"}
                      </Link>
                    </span>
                  ))}
                </p>
              )}
              {coverage.available.length > 0 && (
                <p className="mt-1 text-sm text-ele-body">
                  {coverage.available.length} uncontracted {coverage.available.length === 1 ? "lift" : "lifts"} at this site
                  available to add.
                </p>
              )}

              {coverage.pendingAmendment && (
                <p className="mt-2 rounded-ele border border-ele-blue/40 bg-ele-blue/10 p-2 text-sm text-ele-blue">
                  A lift change is scheduled for {dateOrNotSet(coverage.pendingAmendment.effectiveDate)} (new annual value{" "}
                  {gbp(coverage.pendingAmendment.newAnnualValue)}). Nothing changes until then; it is applied automatically on
                  that date. Cancel it below to undo.
                </p>
              )}

              {coverage.amendments.length > 0 && (
                <div className="mt-3 border-t border-ele-hairline-soft pt-3">
                  <h3 className="mb-2 font-menu text-xs uppercase tracking-wide text-ele-body">Amendment history</h3>
                  <ul className="space-y-2">
                    {coverage.amendments.map((am) => {
                      const isPending = am.status === "pending";
                      return (
                        <li key={am.id} className="flex flex-wrap items-baseline justify-between gap-2 text-sm">
                          <span className="text-ele-strong">
                            {isPending ? (
                              <StatusPill word="Scheduled" tone="blue" className="mr-1" />
                            ) : null}
                            {/* Session AJJ: a link-only row's two figures are equal by construction, so
                                printing "GBP 797 to GBP 797" beside a reprice would read as a nil-effect
                                price change rather than what it is. Read from the row's own flag, never
                                inferred from the figures matching: a real reprice can land on the figure
                                it started from. */}
                            {am.linkOnly ? (
                              <StatusPill word="Link only" tone="purple" className="mr-1" />
                            ) : null}
                            {dateOrNotSet(am.effectiveDate)}:{" "}
                            {am.addedCount > 0 && <span className="text-ele-green">+{am.addedCount} ({am.addedEleNumbers.join(", ")})</span>}{" "}
                            {am.removedCount > 0 && <span className="text-ele-danger">-{am.removedCount} ({am.removedEleNumbers.join(", ")})</span>}{" "}
                            <span className="text-ele-body">
                              {am.linkOnly ? `${gbp(am.newAnnualValue)} unchanged` : `${gbp(am.previousAnnualValue)} to ${gbp(am.newAnnualValue)}`}
                            </span>
                            {am.reason && <span className="text-ele-body"> · {am.reason}</span>}
                          </span>
                          {isAdmin && am.isMostRecent && (
                            <ConfirmButton
                              onConfirm={() => revertAmendment(am.id)}
                              label={isPending ? "Cancel" : "Revert"}
                              confirmLabel={isPending ? "Confirm cancel?" : "Confirm revert?"}
                              busy={revertBusyId === am.id}
                              busyLabel={isPending ? "Cancelling..." : "Reverting..."}
                              className="font-menu text-xs tracking-wide text-ele-body hover:text-ele-danger disabled:opacity-50"
                            />
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              )}
              {coverageError && <p role="alert" className="mt-2 text-sm text-ele-danger">{coverageError}</p>}
            </section>
          )}

          {/* Session ACR: the contract's care equipment (covered at signing; managed on the site's
              Equipment tab, not through the lift flow). Examination state is HONEST: a people-lifting
              item with no recorded exam reads amber "Awaiting first examination record". Session ACV:
              a gates-only tiered contract's items are additional items, never "Care equipment" -- the
              heading follows what the group actually holds. */}
          {coverage && (coverage.equipment?.length ?? 0) > 0 && (
            <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
              <h2 className="mb-2 font-heading text-lg text-ele-strong">
                {coverage.equipment!.every((e) => e.additionalItem)
                  ? "Covered additional items"
                  : coverage.equipment!.some((e) => e.additionalItem)
                    ? "Covered items and care equipment"
                    : "Care equipment"}
              </h2>
              <ul className="space-y-1.5 text-sm">
                {coverage.equipment!.map((e) => (
                  <li key={e.id} className={`flex flex-wrap items-baseline justify-between gap-2 ${e.retiredAt ? "opacity-60" : ""}`}>
                    <span className="text-ele-strong">
                      {[e.name, e.eleNumber].filter(Boolean).join(" | ") || "Equipment"}
                      {e.frequency && (
                        <span className="text-ele-body"> · {EQUIPMENT_FREQ[e.frequency] ?? e.frequency}</span>
                      )}
                    </span>
                    <span className="shrink-0 text-xs">
                      {e.retiredAt ? (
                        <span className="text-ele-body opacity-70">Retired</span>
                      ) : e.lolerDue ? (
                        <span className="text-ele-body">Examination due {dateOrNotSet(e.lolerDue)}</span>
                      ) : e.peopleLifting ? (
                        <span className="text-amber-400">Awaiting first examination record</span>
                      ) : (
                        <span className="text-ele-body opacity-70">No examination required</span>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Session AQW: "Email a covering note" moved to the Renewal tab's own "Renewal letter"
              card (renamed "Draft renewal letter", matching the Renewals-list rename) -- Delete
              contract is the one action left here, since it is a whole-record action, not a renewal
              one. */}
          <div className="flex flex-wrap max-sm:items-center gap-3">
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                setDeleteError(null);
                setDeleteOpen(true);
              }}
            >
              Delete contract
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Session AQW (M-1), Renewal tab, order (a)-(g) from the session's own design:
              a one-line context summary, a plain-English explanation of the two real outcomes, then
              the four panels UNCHANGED (RegisterConfirmationPanel, RenewalLinkPanel,
              RenewalFrequencyPanel, RenewalPriceOverridePanel), then the Renewal letter card. */}
          <section className="mb-4 rounded-ele border border-ele-purple-accent bg-ele-surface p-5">
            <p className="text-sm text-ele-strong">
              {TIER_LABEL[c.tier] ?? c.tier} tier · {gbp(c.annualValue)} a year · renews {dateOrNotSet(c.renewalDate)}
            </p>
            <p className="mt-3 text-sm text-ele-body">
              A renewal ends one of two ways. The price can be reviewed on the current agreement: the rise is confirmed on the{" "}
              <Link to="/office/renewals" className="text-ele-green underline-offset-2 hover:underline">
                Renewals page
              </Link>
              , then the customer is emailed the renewal notice from this page. Or the customer renews online: send their renewal
              link below, and they choose their cover and sign a new agreement.
            </p>
          </section>

          {/* Session AIG ("Renewal A"): the office register-confirmation gate, the precondition for a
              future renewal link. Own data load, so a slow/failed register-review fetch never blocks the
              rest of the page. */}
          <RegisterConfirmationPanel contractId={c.id} />

          {/* Session AII ("Renewal B"): issue and copy the customer renewal link. The link is
              deliberately not returned by any MCP tool (it is a bearer credential for a page that needs
              no login), so this panel is the only place the office can get it. */}
          <RenewalLinkPanel contractId={c.id} />
          {/* Session AIK: renders only when a renewal asked for a cadence change that is still undecided. */}
          <RenewalFrequencyPanel contractId={c.id} />
          {/* Session AKO (Director decision): a manual renewal price, set once a figure has been agreed
              with the customer directly, replacing the CPI calculation for this one cycle. */}
          <RenewalPriceOverridePanel contractId={c.id} />

          {/* Session AQW: the manual covering-letter trigger (mechanism C), moved here from the
              page's old bottom button row and renamed "Draft renewal letter" -- "Email" was never
              accurate, since draftBrandedEmail composes a Gmail DRAFT, never a send. */}
          <section className="ele-card mt-4 p-4">
            <h2 className="mb-2 font-heading text-lg text-ele-strong">Renewal letter</h2>
            <p className="mb-3 text-sm text-ele-body">
              Drafts a letter into Gmail for you to review. It includes the renewal link once one has been issued. Nothing is
              sent.
            </p>
            <button
              onClick={() => setRenewOpen(true)}
              className="rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-strong"
            >
              Draft renewal letter
            </button>
          </section>
        </>
      )}

      {renewOpen && <RenewalModal contract={{ id: c.id, siteName: c.siteName, tier: c.tier }} onClose={() => setRenewOpen(false)} />}

      {/* Session AHN: formal cancellation. On Done the page re-reads: an immediate (5.2)
          cancellation now shows the Cover-ended panel, a 5.1 notice shows the amber notice panel. */}
      {cancelOpen && (
        <CancelContractModal
          contract={{ id: c.id, siteName: c.siteName, customerName: c.customerName, customerEmail: c.customerEmail, cancellation: c.cancellation }}
          onClose={() => setCancelOpen(false)}
          onDone={() => {
            setCancelOpen(false);
            // A fresh notice supersedes any "withdrawn" note still on screen; leaving it would have
            // the page assert a live notice AND its withdrawal at once (review fix).
            setWithdrawNote(null);
            void load();
          }}
        />
      )}

      {/* Session AER: ending cover. Two-step confirm naming the site, per the platform's branded
          confirm rule: this stops billing, changes what every future call-out costs, and opens the
          30 day grace window. */}
      {endOpen && (
        <ModalShell ariaLabel="End contract" onClose={() => !endBusy && setEndOpen(false)}>
          <h2 className="mb-1 font-heading text-xl text-ele-strong">End contract</h2>
          <p className="mb-4 text-sm text-ele-body">
            Record that maintenance cover for {c.siteName} has stopped. For 30 days after the end date, call-outs still
            bill at the agreed contract rate. After that they bill at the published non-contract rate.
          </p>
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="end-date">
            Date cover ended
          </label>
          <input
            id="end-date"
            type="date"
            value={endDate}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(e) => setEndDate(e.target.value)}
            className="mb-1 w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-2 text-sm text-ele-strong"
          />
          <p className="mb-3 text-xs text-ele-body">This cannot be a future date. Record it on the day cover stops.</p>
          <label className="mb-1 block font-menu text-xs uppercase tracking-wide text-ele-body" htmlFor="end-reason">
            Why is cover ending?
          </label>
          <textarea
            id="end-reason"
            rows={3}
            value={endReason}
            onChange={(e) => setEndReason(e.target.value)}
            placeholder="e.g. Customer gave 90 days notice on 1 June."
            className="w-full rounded-ele border border-ele-purple-accent bg-ele-field-inset px-3 py-2 text-sm text-ele-strong"
          />
          {endError && (
            <p role="alert" className="mt-3 text-sm text-ele-danger">
              {endError}
            </p>
          )}
          <div className={`mt-5 gap-3 ${MODAL_ACTIONS}`}>
            <button
              onClick={() => !endBusy && setEndOpen(false)}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body ${MODAL_ACTION_BTN}`}
            >
              Cancel
            </button>
            <ConfirmButton
              onConfirm={() => void endContract()}
              busy={endBusy}
              busyLabel="Ending cover..."
              disabled={endReason.trim().length < 5}
              label="End contract"
              confirmLabel={`Press again to end cover for ${c.siteName}`}
              className={`rounded-ele border border-ele-purple-accent px-4 py-2 font-menu text-xs tracking-wide text-ele-body transition hover:border-ele-green hover:text-ele-green ${MODAL_ACTION_BTN}`}
            />
          </div>
        </ModalShell>
      )}

      {manageOpen && coverage && (
        <ManageLiftsModal
          contractId={c.id}
          coverage={coverage}
          onClose={() => setManageOpen(false)}
          onApplied={(next) => {
            setCoverage(next);
            setManageOpen(false);
            void api.contracts.get(id).then(setContract).catch(() => undefined); // refresh header annual value
          }}
        />
      )}

      {startServiceNowOpen && (
        <StartServiceNowModal
          contractId={c.id}
          siteNameHint={c.siteName}
          onClose={() => setStartServiceNowOpen(false)}
          onCreated={() => void loadCoverage()}
        />
      )}

      <ConfirmDeleteModal
        open={deleteOpen}
        title="Delete contract?"
        message={
          hasBlockers
            ? "This contract is active or has invoices, jobs or LOLER reports on file, so the delete is likely to be blocked. Only an unused, non-active contract can be removed."
            : `Deleting the contract for ${c.siteName} (${c.customerName}) removes it permanently. This cannot be undone.`
        }
        warning={hasBlockers ? "Resolve the attached records first, then delete." : undefined}
        confirmLabel="Delete contract"
        busy={deleteBusy}
        error={deleteError}
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleteBusy) return;
          setDeleteOpen(false);
          setDeleteError(null);
        }}
      />
    </div>
  );
}
